import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CircleNotch as Loader2,
  ArrowsDownUp as ArrowUpDown,
  FunnelSimple as ListFilter,
  X,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { MultiSelect } from '../components/ui/multi-select';
import { RangeCombobox } from '../components/ui/range-combobox';
import { useTranslation } from '../i18n/I18nContext';
import { useShowcaseText } from '../i18n/useShowcaseText';
import { useTranslateVehicleValue } from '../utils/vehicleValues';
import { deliveryCell } from '../utils/deliveryDate';

import { Dropdown } from '../components/ui/dropdown';
import CarCard from '../components/CarCard';
import TestDriveModal from '../components/TestDriveModal';
import DutyFreeCheckbox from '../components/DutyFreeCheckbox';
import AvailableOnlyFilter from '../components/AvailableOnlyFilter';
import SpecialOffersFilter from '../components/SpecialOffersFilter';
import { useCars } from '../context/CarsContext';
import { useDutyFree } from '../context/DutyFreeContext';
import { useVoiceAssistant } from '../context/VoiceAssistantContext';
import { useWidgetMode } from '../context/WidgetModeContext';
import SearchBar from '../components/SearchBar';
import { ShowcaseFilterBar } from './catalog/ShowcaseFilterBar';
import { browserControlWS } from '../services/browserControlWebSocket';
import { searchCarInventory } from '../services/api';
import { isReservedCar, compareSearchRelevance } from '../utils/reservedCars';
import { resolveCatalogSearch } from '../utils/localCarSearch';
import { config } from '../config/runtimeConfig';
import './CarsPage.css';

// Sort options for car listings.
// 'relevance' is meaningful only when ?search= is active (sorts by Meilisearch
// rank); when no search, it degrades to no-op (rank lookup misses → all equal).
const SORT_OPTIONS = [
  { value: 'menu-index', label: 'Default (Recommended)' },
  { value: 'relevance', label: 'Relevance' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'year-desc', label: 'Year: Newest First' },
  { value: 'year-asc', label: 'Year: Oldest First' },
  { value: 'mileage-asc', label: 'Mileage: Low to High' },
  { value: 'mileage-desc', label: 'Mileage: High to Low' },
];

/**
 * Valid `sortBy` values — single source of truth for the embed-level override
 * (#1220). `MiniCatalog` validates `data-sort-by` against this before passing it
 * down, so a typo in the host page falls back to the built-in default instead of
 * landing in the `switch` default branch (which returns 0 = unsorted).
 *
 * Mirrored in b2b-ui `widget-config-form.component.ts::MINI_CATALOG_SORT_OPTIONS`
 * (the admin dropdown) — keep both lists in sync.
 */
export const SORT_VALUES = Object.freeze(SORT_OPTIONS.map((o) => o.value));

/**
 * Приоритет сортировки (#1220):
 *   1. `?sortBy` в URL — посетитель выбрал сам через sort dropdown;
 *   2. `sortByOverride` — дефолт конкретного embed'а, заданный оператором в b2b-ui;
 *   3. built-in: mini-catalog — `price-asc` (промо-витрина "от стольки-то", #828),
 *      полный каталог — `menu-index` (ручной порядок из Inventory).
 */
export function resolveSortBy(urlSortBy, sortByOverride, isMiniCatalog) {
  return (
    urlSortBy || sortByOverride || (isMiniCatalog ? 'price-asc' : 'menu-index')
  );
}

// Fixed price bands for the catalog price filter (client request: single-tap
// bands instead of From/To dropdowns). `min`/`max` map straight onto the
// existing minPrice/maxPrice URL params, so filtering/sorting logic is
// unchanged. `max: null` = open-ended (drop maxPrice, inventory ceiling covers
// it). To tune bands per dealer this list is the single source of truth.
const PRICE_BANDS = [
  { value: '0-10000', label: 'Up to €10k', min: 0, max: 10000 },
  { value: '10000-15000', label: '€10k – €15k', min: 10000, max: 15000 },
  { value: '15000-20000', label: '€15k – €20k', min: 15000, max: 20000 },
  { value: '20000-25000', label: '€20k – €25k', min: 20000, max: 25000 },
  { value: '25000-30000', label: '€25k – €30k', min: 25000, max: 30000 },
  { value: '30000+', label: '€30k+', min: 30000, max: null },
];

// Number of Meilisearch hits used for catalog filter/sort. Keyword search returns
// a long low-relevance tail on short or typo queries (e.g. "toy" → 91 hits where
// only ~15 are real Toyotas). Cutting the tail keeps the catalog focused on what
// Meilisearch actually ranked highly without exposing the noise.
//
// 50, raised from 30 in #1221: sources that opted into showing reserved cars get
// them back in the hit set, and those now consume slots in this window. At 30 a
// broad query on a dealer with many reserved cars ("bmw") could push genuinely
// available cars off the results page entirely — the opposite of the point.
const MEILI_TOP_K = 50;

/**
 * #1206 — a car can legitimately have no year / mileage / price (blank sheet cell).
 * "Absent" must not mean "hidden": the car is shown unless the buyer has ACTUALLY
 * narrowed that field, in which case an unknown value can't be claimed to fall in the
 * requested range and is excluded. `rangeIsActive` mirrors the comparison the
 * filter-count badge uses, since these sliders have no explicit off state — they
 * default to the full computed range.
 *
 * Module scope on purpose: these are pure (every input is passed in), and as
 * in-component `const`s declared BELOW `filteredCars` they sat in the temporal dead
 * zone — `filteredCars` runs eagerly on every render, so reading them threw
 * "Cannot access 'inRange' before initialization" and killed the whole catalog.
 */
function inRange(value, min, max, fullMin, fullMax) {
  const rangeIsActive = min !== fullMin || max !== fullMax;
  if (value == null) return !rangeIsActive;
  return value >= min && value <= max;
}

/** Sort key for an optional numeric field: `missing` is the value that sorts the car to
 *  the bottom for the current direction (#1206). A real 0 (e.g. 0 km) is kept. */
function yearOf(c, missing) {
  return c.year == null ? missing : c.year;
}

function mileageOf(c, missing) {
  return c.mileage == null ? missing : c.mileage;
}

// #1199: a car's body style can be multi-value ("SUV | Crossover" from storage,
// or a legacy "SUV, Crossover" cell). Split into individual tokens so the
// multi-select filter matches on any one, and the option list shows each body
// style separately instead of a combined string.
function splitBodyStyle(value) {
  return (value || '')
    .split(/[|,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const CarsPage = ({
  // #860 single-init embed model: registry components (MiniCatalog/Catalog)
  // прокидывают per-component params как props, обходя race-y window.runtimeConfig.
  // Legacy AppWidget* пути не передают эти props — всё резолвится через config getters.
  maxCarsOverride,
  presetFiltersOverride,
  catalogUrlOverride,
  // Phase 1 (#945, mini-catalog only — full catalog не затрагивается):
  // showDutyFreePricesOverride — включает DF режим (toggle visible + URL sync + built-in
  //   default-ON). Когда true: первый mount авто-применяет `dutyFree=true` если URL пустой.
  // showFiltersPaneOverride — visibility всей filters panel. Когда false — скрываем целиком.
  showDutyFreePricesOverride,
  showFiltersPaneOverride,
  // showMonthlyRatesOverride (#828) — per-embed flag: карточки показывают
  // "from €X/mo*" + полную цену в скобках вместо обычной цены. mini-catalog only.
  showMonthlyRatesOverride,
  // sortByOverride (#1220) — дефолтный порядок карточек для конкретного embed'а,
  // задаётся оператором в b2b-ui. Уступает явному `?sortBy` в URL (посетитель
  // выбрал сам через sort dropdown), но перекрывает built-in mini-catalog дефолт.
  sortByOverride,
} = {}) => {
  // Витрина под `I18nProvider` переводится; в catalog-embed дилера провайдера
  // намеренно нет (см. Catalog.jsx), там `t()` отдаёт английский fallback —
  // то есть ровно то, что было до перевода.
  const { t, language } = useTranslation();
  const showcaseText = useShowcaseText();
  const translateVehicleValue = useTranslateVehicleValue();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Meilisearch facet counts for filter badges (set by SearchBar callback)
  const [meliFacets, setMeliFacets] = useState(null);
  // Meilisearch top-K hit IDs with their original ranking (Map<id, rankIndex>).
  // Drives BOTH the catalog filter AND the sort order when ?search= is active:
  //   - filter:  car shown iff Map.has(id)
  //   - sort:    smaller rankIndex = more relevant = goes first
  // We slice to TOP_K (30) because Meilisearch returns a long tail of low-score
  // matches on short/typo queries (e.g. "toy" → 91 hits where only ~15 are real
  // Toyotas, the rest is noise). null = no search OR no results yet → show all.
  const [meliHitRanks, setMeliHitRanks] = useState(null);
  // Meilisearch `_rankingScore` per hit id (#1234). Separate map so the filter
  // predicates keep using `meliHitRanks` untouched; only the relevance sort reads
  // scores. Null when no search / no results, exactly like the rank map.
  const [meliHitScores, setMeliHitScores] = useState(null);
  const filtersRef = useRef(null);
  const filtersContentRef = useRef(null);
  const filtersPlaceholderRef = useRef(null);

  // Year filter options (7 вариантов)
  const currentYear = new Date().getFullYear();
  const yearFromOptions = [
    0,
    currentYear - 10,
    currentYear - 7,
    currentYear - 5,
    currentYear - 3,
    currentYear - 1,
    currentYear,
  ];
  const yearToOptions = [
    0,
    currentYear - 5,
    currentYear - 3,
    currentYear - 2,
    currentYear - 1,
    currentYear,
    currentYear + 1,
  ];

  const { getAllCars, loading, error } = useCars();
  const allCars = getAllCars();
  const voiceAssistant = useVoiceAssistant();
  const { isDutyFree, setIsDutyFree, calculateDutyFree } = useDutyFree();

  // widgetMode читается из per-instance React Context (useWidgetMode), НЕ из
  // config.WIDGET_MODE: глобальный window.runtimeConfig перезаписывается каждым
  // B2BDemo.mount(), и на страницах с несколькими embed'ами (chat-only footer +
  // mini-catalog homepage) последний mount выигрывал — все embed'ы читали чужой
  // mode. Объявлено здесь (а не только в render) потому что mount-once DF
  // default-ON гейтится на isMiniCatalog (#990) — full catalog не должен по
  // умолчанию показывать VAT-excluded цены.
  const widgetMode = useWidgetMode();
  const isMiniCatalog = widgetMode === 'mini-catalog';
  // Каталог самой витрины (не встройка у дилера). Запрос сюда приходит из
  // поля поиска в герое главной (`?search=`); своей строки поиска у каталога
  // витрины нет. Условие то же, что у заголовка «Our cars» ниже.
  const isSiteCatalog = !isMiniCatalog && config.ENVIRONMENT_NAME !== 'widget';

  // Phase 1 per-embed flag (#945) — приходит из MiniCatalog.parseAttrs:
  // dutyFreePricesActive включает controlled-mode DF toggle с URL sync +
  // встроенный default-ON. Только для mini-catalog (полный каталог не задаёт).
  // showDutyFreeCheckbox объединяет два пути:
  //   - per-embed (mini-catalog) → URL-synced controlled mode
  //   - env/runtime (legacy SHOW_DUTY_FREE_FILTER) → uncontrolled (context-only)
  // filtersPaneVisible вычисляется в render-блоке потому что зависит от
  // `isMiniCatalog` (объявлен выше через useWidgetMode hook).
  const dutyFreePricesActive = showDutyFreePricesOverride === true;
  const showDutyFreeCheckbox =
    dutyFreePricesActive || config.SHOW_DUTY_FREE_FILTER;

  // Stable callback for SearchBar AND for direct fetch path (see useEffect below)
  // — store facets AND top-K hit IDs with ranking.
  // - String(h.id) coercion: Meilisearch returns numeric ids, allCars uses String(id).
  // - slice(0, MEILI_TOP_K): drop the low-score tail so we don't pollute the catalog
  //   with noise.
  // - results.available === false (disabled mode / failed call) → null, not empty
  //   Map, otherwise matchesSearch would block every car.
  const handleSearchResults = useCallback((results) => {
    setMeliFacets(results?.facetDistribution ?? null);
    if (results?.hits && results.available !== false) {
      const ranks = new Map();
      const scores = new Map();
      results.hits.slice(0, MEILI_TOP_K).forEach((h, i) => {
        ranks.set(String(h.id), i);
        // `_rankingScore` arrives on every search since #1235 (it used to be
        // hybrid-only). Absent for an older API — `relevanceBand` then collapses
        // everything into one band, which degrades to "available first".
        if (typeof h._rankingScore === 'number')
          scores.set(String(h.id), h._rankingScore);
      });
      setMeliHitRanks(ranks);
      setMeliHitScores(scores);
    } else {
      setMeliHitRanks(null);
      setMeliHitScores(null);
    }
  }, []);

  // Apply preset filters from mount config on initial load (#783, #828).
  // В mini-catalog mode тоже используется inline presetFilters из mount params
  // (через overrides в mountWithToken) — нет отдельной преsetName/pool логики,
  // т.к. каждый embed в admin хранит свои filters прямо.
  // Mount-once initial URL setup (#945 + #954).
  // Объединённый эффект: preset filters + DF default-ON в ОДНОМ
  // setSearchParams call. Раньше были два mount-once эффекта (preset +
  // dutyFreeDefaultAppliedRef), оба использовали `searchParams` из closure —
  // второй перезатирал результат первого. Callback form
  // `setSearchParams((prev) => ...)` не помогает потому что в React Router 6
  // `searchParamsRef.current` обновляется через свой useEffect ПОСЛЕ обоих
  // navigate calls. Решение: один setSearchParams с merged values.
  const initialUrlAppliedRef = useRef(false);
  useEffect(() => {
    if (initialUrlAppliedRef.current) return;
    const presetFilters = presetFiltersOverride ?? config.PRESET_FILTERS;
    // Default-ON для DF ТОЛЬКО в mini-catalog (#990): full marker catalog тоже
    // получает dutyFreePricesActive (Catalog.jsx прокидывает SHOW_DUTY_FREE_PRICES),
    // но публичный каталог не должен по умолчанию показывать VAT-excluded цены —
    // там toggle стартует OFF, пользователь включает сам. Если preset уже содержит
    // dutyFree, он сам выставит — default не нужен.
    const needDutyFreeDefault =
      dutyFreePricesActive && isMiniCatalog && !presetFilters?.dutyFree;
    if (!presetFilters && !needDutyFreeDefault) return;
    initialUrlAppliedRef.current = true;

    const newParams = new URLSearchParams(searchParams);
    if (presetFilters) {
      // Categorical filters: make, model, bodyStyle, fuelType, transmission
      if (presetFilters.make)
        newParams.set(
          'make',
          Array.isArray(presetFilters.make)
            ? presetFilters.make.join(',')
            : presetFilters.make
        );
      if (presetFilters.model)
        newParams.set(
          'model',
          Array.isArray(presetFilters.model)
            ? presetFilters.model.join(',')
            : presetFilters.model
        );
      if (presetFilters.bodyStyle)
        newParams.set(
          'bodyStyle',
          Array.isArray(presetFilters.bodyStyle)
            ? presetFilters.bodyStyle.join(',')
            : presetFilters.bodyStyle
        );
      if (presetFilters.fuelType)
        newParams.set(
          'fuelType',
          Array.isArray(presetFilters.fuelType)
            ? presetFilters.fuelType.join(',')
            : presetFilters.fuelType
        );
      if (presetFilters.transmission)
        newParams.set(
          'transmission',
          Array.isArray(presetFilters.transmission)
            ? presetFilters.transmission.join(',')
            : presetFilters.transmission
        );
      // Range filters
      if (presetFilters.minPrice)
        newParams.set('minPrice', String(presetFilters.minPrice));
      if (presetFilters.maxPrice)
        newParams.set('maxPrice', String(presetFilters.maxPrice));
      if (presetFilters.minYear)
        newParams.set('minYear', String(presetFilters.minYear));
      if (presetFilters.maxYear)
        newParams.set('maxYear', String(presetFilters.maxYear));
      // Boolean filters
      if (presetFilters.availableOnly) newParams.set('availableOnly', 'true');
      if (presetFilters.isHotOffer) newParams.set('isHotOffer', 'true');
      if (presetFilters.dutyFree) newParams.set('dutyFree', 'true');
      // Text search preset is handled by SearchBar's initialValue prop
    }
    // DF default-ON: применяем только если preset не задал dutyFree уже
    if (needDutyFreeDefault && !newParams.has('dutyFree')) {
      newParams.set('dutyFree', 'true');
    }
    setSearchParams(newParams);
    // DF теперь чистый price-toggle (#990): синхронизируем context сразу на mount,
    // чтобы цены пересчитались для ВСЕХ машин (ничего не скрывая). Критично для
    // preset-only mini-catalog без showDutyFreePrices — там URL→context sync effect
    // ниже early-return'ит (dutyFreePricesActive=false), иначе preset.dutyFree
    // вообще не переключил бы цены.
    if (presetFilters?.dutyFree || needDutyFreeDefault) {
      setIsDutyFree(true);
    }
    console.log('[CarsPage] Applied initial URL params:', {
      presetFilters,
      needDutyFreeDefault,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // URL → context sync для Duty Free toggle (#945, Phase 1).
  // Запускается ТОЛЬКО на изменение searchParams (не на isDutyFree), чтобы не
  // откатывать клик пользователя в момент когда URL ещё не обновлён. Handler
  // обновляет URL и context атомарно — после обоих сетов эффект перепроверит
  // и no-op (ctx=URL уже).
  // Сценарии:
  //  - refresh с `?dutyFree=true` → ctx=true восстановлен (DF prices — price-only, no filter)
  //  - default-ON effect записал URL `dutyFree=true` → ctx=true автоматически
  //  - back/forward в браузере → ctx подстраивается под URL
  useEffect(() => {
    if (!dutyFreePricesActive) return;
    const urlValue = searchParams.get('dutyFree') === 'true';
    if (urlValue !== isDutyFree) {
      setIsDutyFree(urlValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, dutyFreePricesActive]);

  // Controlled handler для DutyFreeCheckbox в per-embed mode — обновляет URL И
  // context одновременно. URL — для refresh-resistance / shareable links, context —
  // для price display в карточках (DF-recalculated цены, без фильтрации машин).
  const handleDutyFreeToggle = (checked) => {
    setIsDutyFree(checked);
    const newParams = new URLSearchParams(searchParams);
    if (checked) {
      newParams.set('dutyFree', 'true');
    } else {
      newParams.delete('dutyFree');
    }
    setSearchParams(newParams);
  };

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Высота хедера: читается из CSS переменной --header-height
  // Устанавливается в widget.jsx на основе config.headerHeight или config.showHeader
  const getHeaderHeight = () => {
    // Ищем CSS переменную в Shadow DOM root или в document.documentElement
    const shadowRoot = filtersRef.current?.getRootNode();
    const rootElement =
      shadowRoot instanceof ShadowRoot
        ? shadowRoot.getElementById('b2b-demo-root') || shadowRoot.host
        : document.documentElement;

    if (rootElement) {
      const cssVar = getComputedStyle(rootElement)
        .getPropertyValue('--header-height')
        .trim();
      if (cssVar) {
        const parsed = parseInt(cssVar, 10);
        if (!isNaN(parsed)) return parsed;
      }
    }
    // Fallback: высота хедера виджета (64px) или 0 если хедер скрыт
    return config.SHOW_HEADER ? 64 : 0;
  };

  // Sticky filters on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!filtersRef.current || !filtersPlaceholderRef.current) return;

      const placeholder = filtersPlaceholderRef.current;
      const placeholderRect = placeholder.getBoundingClientRect();
      const headerHeight = getHeaderHeight();

      // Становимся sticky когда placeholder уходит за хедер
      if (placeholderRect.top <= headerHeight) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
        // Reset mobile filters when scrolling back to top
        if (isMobile) {
          setIsMobileFiltersOpen(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // Close mobile filters on click outside
  useEffect(() => {
    if (!isMobile || !isMobileFiltersOpen) return;

    const handleClickOutside = (e) => {
      if (!filtersRef.current) return;

      // Используем composedPath() для корректной работы с portal элементами
      // (dropdowns рендерятся через portal вне filtersRef)
      const path = e.composedPath ? e.composedPath() : [e.target];
      const isInsideFilters = path.includes(filtersRef.current);
      const isInsideDropdown = path.some(
        (el) => el.dataset?.portalDropdown === 'true'
      );

      if (!isInsideFilters && !isInsideDropdown) {
        setIsMobileFiltersOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobile, isMobileFiltersOpen]);

  // Scroll filter panel to top when mobile filter panel opens
  useEffect(() => {
    if (isMobileFiltersOpen && filtersRef.current) {
      filtersRef.current.scrollTop = 0;
    }
  }, [isMobileFiltersOpen]);

  // Вычисляем диапазон цен из всех автомобилей
  const priceRange = useMemo(() => {
    const prices = allCars
      .filter((car) => car.price > 0)
      .map((car) => car.price);
    if (prices.length === 0) return { min: 0, max: 100000 };
    return {
      min: Math.floor(Math.min(...prices) / 1000) * 1000,
      max: Math.ceil(Math.max(...prices) / 1000) * 1000,
    };
  }, [allCars]);

  // Вычисляем диапазон пробега из всех автомобилей
  const mileageRange = useMemo(() => {
    const mileages = allCars
      .filter((car) => car.mileage > 0)
      .map((car) => car.mileage);
    if (mileages.length === 0) return { min: 0, max: 200000 };
    return {
      min: Math.floor(Math.min(...mileages) / 1000) * 1000,
      max: Math.ceil(Math.max(...mileages) / 1000) * 1000,
    };
  }, [allCars]);

  // Генератор filter options — опции подстраиваются под фактический диапазон
  // инвентаря. Без этого hard-coded up-to-300k выглядел нелепо у дилера где
  // большинство машин до 25k.
  // Выбираем шаг пропорционально max, чтобы получить ~4-7 «круглых» опций.
  const buildRangeOptions = (max) => {
    if (!max || max <= 0) return [0];
    let step;
    if (max <= 30000) step = 5000;
    else if (max <= 60000) step = 10000;
    else if (max <= 150000) step = 25000;
    else if (max <= 300000) step = 50000;
    else step = 100000;
    const niceMax = Math.ceil(max / step) * step;
    const opts = [0]; // "Any"
    for (let v = step; v <= niceMax; v += step) opts.push(v);
    return opts;
  };

  // Mileage — тот же подход
  const mileageFromOptions = useMemo(
    () => buildRangeOptions(mileageRange.max),
    [mileageRange.max]
  );
  const mileageToOptions = mileageFromOptions;

  // Вычисляем диапазон годов из всех автомобилей
  const yearRange = useMemo(() => {
    const years = allCars.filter((car) => car.year > 0).map((car) => car.year);
    if (years.length === 0) return { min: 2000, max: new Date().getFullYear() };
    return {
      min: Math.min(...years),
      max: Math.max(...years),
    };
  }, [allCars]);

  // Читаем фильтры из URL или используем значения по умолчанию
  const searchTerm = searchParams.get('search') || '';

  // Direct Meilisearch fetch for the embed scenario where SearchBar is hidden
  // (`SHOW_SEARCH_BAR=false`) but the URL still carries `?search=` from a
  // standalone `data-shiftgears="search"` Enter redirect. Without this, the
  // catalog would have no way to learn what Meilisearch matches and would just
  // show everything. When SearchBar IS visible it owns the fetch via its own
  // debounced effect; we skip here to avoid double requests.
  //
  // `force: true` bypasses the `WIDGET_SEARCH_MODE='disabled'` short-circuit
  // in `searchCarInventory()`. The presence of `?search=` in the URL means a
  // standalone `<Search>` marker on the host page already opted in to
  // Meilisearch — catalog is just consuming the result here, same gate as
  // the standalone Search component which also passes force:true.
  //
  // Витрина (`isSiteCatalog`) получает запрос из поля поиска в герое. Когда
  // Meilisearch на сервере выключен (#1344), она фильтрует по загруженному
  // стоку — тем же правилом, что подсказки в герое; у дилеров поведение
  // прежнее.
  useEffect(() => {
    if (config.SHOW_SEARCH_BAR) return;
    if (!searchTerm) {
      handleSearchResults(null);
      return;
    }
    let cancelled = false;
    const term = searchTerm.trim();
    searchCarInventory(term, { limit: 100, force: true })
      .then((result) => {
        if (cancelled) return;
        handleSearchResults(
          resolveCatalogSearch(result, { isSiteCatalog, cars: allCars, term })
        );
      })
      .catch(() => {
        if (!cancelled) handleSearchResults(null);
      });
    return () => {
      cancelled = true;
    };
  }, [searchTerm, handleSearchResults, isSiteCatalog, allCars]);

  // Множественные фильтры хранятся как строки через запятую (например: "BMW,Audi")
  const makeFilter = searchParams.get('make')
    ? searchParams.get('make').split(',')
    : [];
  const modelFilter = searchParams.get('model')
    ? searchParams.get('model').split(',')
    : [];
  const bodyStyleFilter = searchParams.get('bodyStyle')
    ? searchParams.get('bodyStyle').split(',')
    : [];
  const fuelTypeFilter = searchParams.get('fuelType')
    ? searchParams.get('fuelType').split(',')
    : [];
  const transmissionFilter = searchParams.get('transmission')
    ? searchParams.get('transmission').split(',')
    : [];
  // Дефолтные значения цены округляем до шага 5000
  const defaultMinPrice = Math.floor(priceRange.min / 5000) * 5000;
  const defaultMaxPrice = Math.ceil(priceRange.max / 5000) * 5000;
  const minPrice = parseInt(searchParams.get('minPrice') || defaultMinPrice);
  const maxPrice = parseInt(searchParams.get('maxPrice') || defaultMaxPrice);
  // Price band selector — derive the active band from the raw minPrice/maxPrice
  // params. Non-band ranges (e.g. an agent-set custom range) fall through to ''
  // ("Any price") in the trigger while cars stay filtered.
  const rawMinPrice = searchParams.get('minPrice');
  const rawMaxPrice = searchParams.get('maxPrice');
  const selectedPriceBand = (() => {
    const min = rawMinPrice === null ? null : parseInt(rawMinPrice, 10);
    const max = rawMaxPrice === null ? null : parseInt(rawMaxPrice, 10);
    const band = PRICE_BANDS.find(
      (b) => b.min === min && (b.max ?? null) === max
    );
    return band ? band.value : '';
  })();
  const priceBandOptions = useMemo(
    () => [
      { value: '', label: t('showcase.catalog.anyPrice', 'Any price') },
      ...PRICE_BANDS.map((b) => ({ value: b.value, label: b.label })),
    ],
    [t]
  );
  const handlePriceBandChange = (bandValue) => {
    const newParams = new URLSearchParams(searchParams);
    const band = PRICE_BANDS.find((b) => b.value === bandValue);
    if (!band) {
      newParams.delete('minPrice');
      newParams.delete('maxPrice');
    } else {
      newParams.set('minPrice', String(band.min));
      if (band.max === null) newParams.delete('maxPrice');
      else newParams.set('maxPrice', String(band.max));
    }
    setSearchParams(newParams);
  };
  const minMileage = parseInt(
    searchParams.get('minMileage') || mileageRange.min
  );
  const maxMileage = parseInt(
    searchParams.get('maxMileage') || mileageRange.max
  );
  const minYear = parseInt(searchParams.get('minYear') || yearRange.min);
  const maxYear = parseInt(searchParams.get('maxYear') || yearRange.max);
  // Mini-catalog (#828): дефолт сортировки — цена по возрастанию (price-asc),
  // т.к. это промо-витрина "от стольки-то" и логично показать самые доступные
  // первыми. Полный каталог сохраняет menu-index. Явный ?sortBy в URL (если в
  // embed'е показан sort dropdown) имеет приоритет над дефолтом.
  //
  // #1220: между ними — per-embed `sortByOverride` из admin-панели (см.
  // resolveSortBy). Для mini-catalog оператор может выбрать menu-index и тем
  // самым отдать управление порядком drag&drop-у в Inventory → Catalog.
  const sortBy = resolveSortBy(
    searchParams.get('sortBy'),
    sortByOverride,
    isMiniCatalog
  );
  const availableOnly = searchParams.get('availableOnly') === 'true';
  const specialOffers = searchParams.get('specialOffers') === 'true';
  // Preset boolean filters (from mount config for embedded catalogs)
  const hotOfferOnly = searchParams.get('isHotOffer') === 'true';
  // NB: dutyFree больше НЕ фильтр (#990) — это чистый price-toggle. Состояние
  // живёт в isDutyFree (context), URL `dutyFree=true` нужен только для
  // refresh/share-resistance controlled toggle'а, ни одну машину не скрывает.

  // Скрытые фильтры по OfferID (для агента, не видны в UI)
  // offerId  — одиночный ID (backward compat, pre-selected car из URL)
  // offerIds — массив ID через запятую (используется агентом для редиректа на
  //            набор машин, найденных через Meilisearch, когда фильтры не
  //            воспроизводимы атрибутами)
  const hiddenOfferIdFilter = searchParams.get('offerId');
  const hiddenOfferIdsFilter = (searchParams.get('offerIds') || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  // Текст chip'а для агентской выборки (set_filters → offerIdsLabel).
  // Chip показывается когда активен сам id-фильтр; label лишь задаёт текст,
  // без него — generic "AI selection".
  const offerIdsLabel = searchParams.get('offerIdsLabel') || '';
  const hasAgentSelection =
    Boolean(hiddenOfferIdFilter) || hiddenOfferIdsFilter.length > 0;

  // Подсчёт активных дополнительных фильтров
  const secondaryFiltersCount = useMemo(() => {
    let count = 0;
    if (modelFilter.length > 0) count++;
    if (bodyStyleFilter.length > 0) count++;
    if (fuelTypeFilter.length > 0) count++;
    if (minMileage !== mileageRange.min || maxMileage !== mileageRange.max)
      count++;
    return count;
  }, [
    modelFilter,
    bodyStyleFilter,
    fuelTypeFilter,
    minMileage,
    maxMileage,
    mileageRange,
  ]);

  // Подсчёт ВСЕХ активных фильтров (для мобильного бейджа)
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (makeFilter.length > 0) count++;
    if (modelFilter.length > 0) count++;
    if (bodyStyleFilter.length > 0) count++;
    if (fuelTypeFilter.length > 0) count++;
    if (minPrice !== defaultMinPrice || maxPrice !== defaultMaxPrice) count++;
    if (minMileage !== mileageRange.min || maxMileage !== mileageRange.max)
      count++;
    if (minYear !== yearRange.min || maxYear !== yearRange.max) count++;
    if (availableOnly) count++;
    if (specialOffers) count++;
    // isDutyFree НЕ считается активным фильтром (#990) — это price-toggle, не фильтр.
    return count;
  }, [
    makeFilter,
    modelFilter,
    bodyStyleFilter,
    fuelTypeFilter,
    minPrice,
    maxPrice,
    defaultMinPrice,
    defaultMaxPrice,
    minMileage,
    maxMileage,
    mileageRange,
    minYear,
    maxYear,
    yearRange,
    availableOnly,
    specialOffers,
  ]);

  // Проверка наличия активных фильтров (для скрытия кнопки Clear)
  const hasAnyFilters = useMemo(() => {
    return (
      makeFilter.length > 0 ||
      modelFilter.length > 0 ||
      bodyStyleFilter.length > 0 ||
      fuelTypeFilter.length > 0 ||
      minPrice !== defaultMinPrice ||
      maxPrice !== defaultMaxPrice ||
      minMileage !== mileageRange.min ||
      maxMileage !== mileageRange.max ||
      minYear !== yearRange.min ||
      maxYear !== yearRange.max ||
      availableOnly ||
      specialOffers
      // isDutyFree исключён (#990): price-toggle, не фильтр — не влияет на Clear.
    );
  }, [
    makeFilter,
    modelFilter,
    bodyStyleFilter,
    fuelTypeFilter,
    minPrice,
    maxPrice,
    defaultMinPrice,
    defaultMaxPrice,
    minMileage,
    maxMileage,
    mileageRange,
    minYear,
    maxYear,
    yearRange,
    availableOnly,
    specialOffers,
  ]);

  // Функция для обновления URL параметров
  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const handleBookTestDrive = (car) => {
    setSelectedCar(car);
    setIsModalOpen(true);
  };

  // Функция для получения машин с применением всех фильтров кроме указанного
  const getFilteredCarsExcept = (exceptFilter) => {
    return allCars.filter((car) => {
      // Meilisearch hit IDs are the source of truth when ?search= is set.
      // null ranks = either no query, or results haven't arrived → don't filter.
      const matchesSearch =
        exceptFilter === 'search'
          ? true
          : searchTerm === '' || !meliHitRanks || meliHitRanks.has(car.id);

      const matchesMake =
        exceptFilter === 'make'
          ? true
          : makeFilter.length === 0 || makeFilter.includes(car.make);
      const matchesModel =
        exceptFilter === 'model'
          ? true
          : modelFilter.length === 0 || modelFilter.includes(car.model);
      const matchesBodyStyle =
        exceptFilter === 'bodyStyle'
          ? true
          : bodyStyleFilter.length === 0 ||
            splitBodyStyle(car.bodyStyle).some((b) =>
              bodyStyleFilter.includes(b)
            );
      const matchesFuelType =
        exceptFilter === 'fuelType'
          ? true
          : fuelTypeFilter.length === 0 ||
            fuelTypeFilter.includes(car.fuelType);
      const matchesTransmission =
        exceptFilter === 'transmission'
          ? true
          : transmissionFilter.length === 0 ||
            transmissionFilter.includes(car.transmission);
      const matchesPrice =
        exceptFilter === 'price'
          ? true
          : inRange(
              car.price || null,
              minPrice,
              maxPrice,
              defaultMinPrice,
              defaultMaxPrice
            );
      const matchesMileage =
        exceptFilter === 'mileage'
          ? true
          : inRange(
              car.mileage,
              minMileage,
              maxMileage,
              mileageRange.min,
              mileageRange.max
            );
      const matchesYear =
        exceptFilter === 'year'
          ? true
          : inRange(car.year, minYear, maxYear, yearRange.min, yearRange.max);
      const matchesAvailable =
        exceptFilter === 'availableOnly'
          ? true
          : !availableOnly || !isReservedCar(car);

      return (
        matchesSearch &&
        matchesMake &&
        matchesModel &&
        matchesBodyStyle &&
        matchesFuelType &&
        matchesTransmission &&
        matchesPrice &&
        matchesMileage &&
        matchesYear &&
        matchesAvailable
      );
    });
  };

  // Фильтруем автомобили
  const filteredCars = allCars.filter((car) => {
    // Скрытый OfferID фильтр (ПЕРВЫЙ приоритет - для агента).
    // Если оба переданы — работают AND (intersection).
    const matchesHiddenOffer =
      (!hiddenOfferIdFilter || car.id === hiddenOfferIdFilter) &&
      (hiddenOfferIdsFilter.length === 0 ||
        hiddenOfferIdsFilter.includes(car.id));

    // Meilisearch hit IDs are the source of truth when ?search= is set.
    // null ranks = either no query, or results haven't arrived → don't filter.
    const matchesSearch =
      searchTerm === '' || !meliHitRanks || meliHitRanks.has(car.id);

    // Множественные фильтры: если массив пустой - показываем все, иначе проверяем вхождение
    const matchesMake =
      makeFilter.length === 0 || makeFilter.includes(car.make);
    const matchesModel =
      modelFilter.length === 0 || modelFilter.includes(car.model);
    const matchesBodyStyle =
      bodyStyleFilter.length === 0 ||
      splitBodyStyle(car.bodyStyle).some((b) => bodyStyleFilter.includes(b));
    const matchesFuelType =
      fuelTypeFilter.length === 0 || fuelTypeFilter.includes(car.fuelType);
    const matchesTransmission =
      transmissionFilter.length === 0 ||
      transmissionFilter.includes(car.transmission);

    // Фильтруем по диапазону цен
    const matchesPrice = inRange(
      car.price || null,
      minPrice,
      maxPrice,
      defaultMinPrice,
      defaultMaxPrice
    );

    // Фильтруем по диапазону пробега
    const matchesMileage = inRange(
      car.mileage,
      minMileage,
      maxMileage,
      mileageRange.min,
      mileageRange.max
    );

    // Фильтруем по диапазону годов
    const matchesYear = inRange(
      car.year,
      minYear,
      maxYear,
      yearRange.min,
      yearRange.max
    );

    // Фильтр Available Only - скрываем проданные/зарезервированные машины
    const matchesAvailable = !availableOnly || !isReservedCar(car);
    // Hot offer фильтр — срабатывает ЛИБО от UI toggle (specialOffers=true),
    // ЛИБО от preset URL-параметра isHotOffer=true (для встраиваемого каталога).
    // Оба источника имеют одинаковую семантику: оставить только машины с HOT бейджем.
    const hotOfferFilterActive = specialOffers || hotOfferOnly;
    const matchesHotOffer = !hotOfferFilterActive || car.isHotOffer === true;
    // Duty Free (#990): НЕ фильтр. Ни toggle, ни preset.dutyFree больше не скрывают
    // машины — DF переключает только отображаемую цену (CarCard читает isDutyFree из
    // контекста и показывает DF-recalculated значение, fallback на обычную цену для
    // машин без dutyFreePrice). Поэтому здесь нет matchesDutyFree* предикатов.

    return (
      matchesHiddenOffer &&
      matchesSearch &&
      matchesMake &&
      matchesModel &&
      matchesBodyStyle &&
      matchesFuelType &&
      matchesTransmission &&
      matchesPrice &&
      matchesMileage &&
      matchesYear &&
      matchesAvailable &&
      matchesHotOffer
    );
  });

  // Эффективная сортировка: при активном поиске дефолтный 'menu-index' автоматом
  // повышается до 'relevance' (иначе menu-index перетасовал бы релевантные машины
  // с низкорейтинговым tail — см. #956). Явный выбор юзера (price-asc, year-desc...)
  // уважается всегда.
  const effectiveSortBy =
    searchTerm && meliHitRanks && sortBy === 'menu-index'
      ? 'relevance'
      : sortBy;

  // Сортировка по цене должна совпадать с ОТОБРАЖАЕМОЙ ценой: при включённом DF
  // карточки показывают DF-цену (calculateDutyFree), поэтому и сортируем по ней —
  // иначе порядок (по обычной цене car.price) не совпадает с тем, что видит
  // пользователь (BF mini-catalog: €9,950 / €10,250 / €9,950 «вне сортировки»).
  const priceOf = (c) => (isDutyFree ? calculateDutyFree(c) : c.price);
  // 0/absent price = "Contact for price" — sort it last rather than as the cheapest (#1206).
  // priceForSort stays in the component — priceOf closes over the duty-free context.
  const priceForSort = (c, missing) => priceOf(c) || missing;

  // Hoisted OUT of the comparator on purpose: as an inline literal it allocated a
  // fresh object plus two closures on every comparison — ~1.7k allocations per
  // sort at 90 cars — and the sort re-runs on every render, including each
  // debounce-driven search re-render.
  const relevanceLookup = {
    score: (car) => meliHitScores?.get(car.id),
    rank: (car) => meliHitRanks?.get(car.id),
  };

  // Сортируем отфильтрованные автомобили
  const sortedCars = [...filteredCars].sort((a, b) => {
    switch (effectiveSortBy) {
      case 'relevance': {
        // Relevance BAND → buyable first → exact Meilisearch order (#1234).
        //
        // Was a flat "+5 positions" penalty for reserved cars. Measuring on test
        // killed that: `q=yaris` returns 8 cars all scoring 0.999–1.000, so the two
        // reserved ones simply held ranks 0–1 and a 5-place shift parked them in the
        // MIDDLE of the available cars. Positions don't carry relevance; scores do.
        // Same band ⇒ every available car first. Different band ⇒ relevance wins, so
        // a reserved exact match still outranks an unrelated available car — the
        // #1221 complaint this whole thread started from.
        //
        // Applies to THIS sort only: an explicit price/year/mileage choice wins,
        // same as browsing.
        return compareSearchRelevance(a, b, relevanceLookup);
      }
      case 'menu-index': {
        // Сортировка по menuIndex (по убыванию), если поле отсутствует - в конец
        const indexA = a.menuIndex ?? Number.MIN_SAFE_INTEGER;
        const indexB = b.menuIndex ?? Number.MIN_SAFE_INTEGER;
        return indexB - indexA;
      }
      case 'price-asc':
        return priceForSort(a, Infinity) - priceForSort(b, Infinity);
      case 'price-desc':
        return priceForSort(b, -Infinity) - priceForSort(a, -Infinity);
      // A car with no year (#1206) sorts last instead of yielding a NaN comparator.
      case 'year-desc':
        return yearOf(b, -Infinity) - yearOf(a, -Infinity);
      case 'year-asc':
        return yearOf(a, Infinity) - yearOf(b, Infinity);
      // Absent mileage sorts last either way (#1206); a real 0 km stays the lowest.
      case 'mileage-asc':
        return mileageOf(a, Infinity) - mileageOf(b, Infinity);
      case 'mileage-desc':
        return mileageOf(b, -Infinity) - mileageOf(a, -Infinity);
      default:
        return 0;
    }
  });

  // Mini-catalog mode (#828): обрезаем до MAX_CARS если задан лимит.
  // В обычном full/chat-only режиме MAX_CARS всегда null → displayedCars === sortedCars.
  // #860: maxCarsOverride от registry MiniCatalog имеет приоритет над config.
  const maxCars = maxCarsOverride ?? config.MAX_CARS;
  const displayedCars =
    maxCars != null ? sortedCars.slice(0, maxCars) : sortedCars;

  // Mini-catalog: hard redirect на каталог клиентского сайта при клике на карточку.
  // CATALOG_URL задан только в mode='mini-catalog'; в обычном режиме → undefined →
  // CarCard использует стандартную react-router navigate (в полном приложении есть VDP route).
  // #860: catalogUrlOverride от registry MiniCatalog имеет приоритет над config.
  //
  // Hardening: mini-catalog НЕ монтирует VDP route (MemoryRouter без /cars/:id),
  // поэтому без catalogUrl клик по карточке — silent no-op. Фоллбэчимся на
  // DEALER_CATALOG_URL, а если и его нет — warn, чтобы мисконфиг (забытый
  // data-catalog-url на маркере) был виден, а не молча ломал "View Details".
  const catalogUrl =
    catalogUrlOverride ??
    config.CATALOG_URL ??
    (isMiniCatalog ? config.DEALER_CATALOG_URL : null);

  if (isMiniCatalog && !catalogUrl) {
    console.warn(
      '[MiniCatalog] No catalogUrl (data-catalog-url) and no DEALER_CATALOG_URL fallback — "View Details" clicks will not navigate.'
    );
  }

  const handleCardClick = catalogUrl
    ? (car) => {
        // Подставляем id в URL вида "<catalogUrl>#/cars/<id>"
        const base = `${catalogUrl.replace(
          /\/+$/,
          ''
        )}#/cars/${encodeURIComponent(car.id)}`;
        const params = new URLSearchParams();
        // #828: переносим Duty Free state через hard redirect, чтобы VDP на
        // целевом каталоге открылся с уже включённым BF toggle. CarDetailPage
        // читает `?dutyFree=true` на mount (gated: только если на целевом embed
        // включён DF — иначе параметр игнорируется, не задевает другие embeds).
        if (isDutyFree) params.set('dutyFree', 'true');
        // backTo: страница, с которой открыли VDP (напр. /british-forces-cars).
        // "Back to Cars" на VDP вернёт сюда (full-page nav), а не на catalogUrl/cars.
        // Берём URL хост-страницы без hash (mini-catalog внутри MemoryRouter —
        // host hash не наш роут). CarDetailPage чтит backTo только same-origin.
        try {
          params.set('backTo', window.location.href.split('#')[0]);
        } catch (_) {
          /* ignore */
        }
        const qs = params.toString();
        window.location.href = qs ? `${base}?${qs}` : base;
      }
    : undefined;

  // Экспортируем отфильтрованные авто для browser control
  useEffect(() => {
    window.currentFilteredCars = {
      cars: sortedCars,
      total: sortedCars.length,
      // Issue #714: Full inventory reference for zero-results diagnosis.
      // Used by _diagnoseZeroResults() to compare filter values against real inventory
      // (faceted validation: e.g. "which models exist for BMW?").
      // This is a JS reference to the same array in React state — NOT a copy,
      // so no extra memory allocation. Current inventory is ~90 cars (~50KB if serialized).
      // TODO: If inventory grows to 1000+ cars, replace with pre-computed
      // inventoryEnums (unique values per field) and inventoryRanges (min/max per range field)
      // to avoid holding the full array on window. Faceted diagnosis would need
      // a different approach (e.g. pre-built make→models map).
      allCars,
      allCarsTotal: allCars.length,
      visibleOfferIds: sortedCars.map((car) => car.id), // Все видимые offer IDs
      timestamp: Date.now(),
    };
  }, [sortedCars, allCars]);

  // Отслеживать изменения фильтров и отправлять агенту
  const filtersInitializedRef = useRef(false);
  useEffect(() => {
    // Пропустить первый рендер
    if (!filtersInitializedRef.current) {
      filtersInitializedRef.current = true;
      return;
    }

    // Пропустить, если фильтры изменяются по команде агента
    if (window.__agentCommandInProgress) {
      console.log(
        '[CarsPage] Skipping filter-changed event (agent command in progress)'
      );
      return;
    }

    // Собрать активные фильтры
    const activeFilters = {};
    if (makeFilter.length > 0) activeFilters.make = makeFilter;
    if (modelFilter.length > 0) activeFilters.model = modelFilter;
    if (bodyStyleFilter.length > 0) activeFilters.bodyStyle = bodyStyleFilter;
    if (fuelTypeFilter.length > 0) activeFilters.fuelType = fuelTypeFilter;
    if (availableOnly) activeFilters.availableOnly = true;

    if (minPrice !== priceRange.min || maxPrice !== priceRange.max) {
      activeFilters.price = { min: minPrice, max: maxPrice };
    }
    if (minMileage !== mileageRange.min || maxMileage !== mileageRange.max) {
      activeFilters.mileage = { min: minMileage, max: maxMileage };
    }
    if (minYear !== yearRange.min || maxYear !== yearRange.max) {
      activeFilters.year = { min: minYear, max: maxYear };
    }

    // Отправить событие изменения фильтров (только от пользователя)
    if (!browserControlWS.isConnected()) {
      console.warn(
        '[CarsPage] Browser control not connected - filter event not sent. Open voice assistant to enable tracking.'
      );
    }

    browserControlWS.sendUserAction('filter-changed', {
      activeFilters,
      filteredCount: sortedCars.length,
      totalCount: allCars.length,
      topCars: sortedCars.slice(0, 10).map((car) => ({
        id: car.id,
        make: car.make,
        model: car.model,
        year: car.year,
        price: car.price,
        mileage: car.mileage,
        fuelType: car.fuelType,
        transmission: car.transmission,
        statusSold: car.statusSold || null,
        // #1488: the agent shows it as the car's arrival.
        deliveryDate: deliveryCell(car.deliveryDate),
      })),
    });
  }, [
    makeFilter,
    modelFilter,
    bodyStyleFilter,
    fuelTypeFilter,
    availableOnly,
    minPrice,
    maxPrice,
    minMileage,
    maxMileage,
    minYear,
    maxYear,
    sortedCars.length,
    allCars.length,
    priceRange.min,
    priceRange.max,
    mileageRange.min,
    mileageRange.max,
    yearRange.min,
    yearRange.max,
  ]);

  // Вычисляем доступные опции для каждого фильтра с учетом других фильтров
  const uniqueMakes = [
    ...new Set(getFilteredCarsExcept('make').map((car) => car.make)),
  ]
    .filter(Boolean)
    .sort();
  const uniqueBodyStyles = [
    ...new Set(
      getFilteredCarsExcept('bodyStyle').flatMap((car) =>
        splitBodyStyle(car.bodyStyle)
      )
    ),
  ]
    .filter(Boolean)
    .sort();
  const uniqueFuelTypes = [
    ...new Set(getFilteredCarsExcept('fuelType').map((car) => car.fuelType)),
  ]
    .filter(Boolean)
    .sort();

  // Facet counts from Meilisearch — показываем количество совпадений рядом с каждым фильтром
  const facets = meliFacets;
  const uniqueTransmissions = [
    ...new Set(
      getFilteredCarsExcept('transmission').map((car) => car.transmission)
    ),
  ]
    .filter(Boolean)
    .sort();

  // Переведённые опции — по алфавиту ПОДПИСЕЙ, а не английских значений:
  // иначе во fr «Break» стоял бы после «Monospace», потому что Estate < MPV
  // (ревью !1296). Пересортировываем, только если перевод что-то поменял: у
  // дилера подписи равны значениям, и порядок остаётся прежним побайтно.
  const byLabel = (options, field) => {
    const labels = options.map((v) => translateVehicleValue(v, field));
    if (labels.every((label, i) => label === options[i])) return options;
    return options
      .map((v, i) => [v, labels[i]])
      // Алфавит языка витрины, а не браузера.
      .sort((a, b) => a[1].localeCompare(b[1], language))
      .map(([v]) => v);
  };

  // Получаем модели с учетом всех фильтров кроме model
  const availableModels = [
    ...new Set(getFilteredCarsExcept('model').map((car) => car.model)),
  ].sort();

  const clearFilters = () => {
    setSearchParams({});
  };

  // Шапка фильтров витрины (#1335, доска «B2 · Two rows»): строка поиска и
  // строка быстрых чипов. У дилера и в mini-catalog — прежние ряды фильтров.
  const showcaseBar = config.IS_OWN_STOCK && !isMiniCatalog;

  // Сменили фильтр, стоя в глубине выдачи (панель прилипла), — возвращаем
  // страницу к началу результатов, под фильтры (#1335). Иначе выдача
  // менялась у верхнего края, и первый ряд новых машин оказывался под
  // прилипшей панелью. Сортировка и поиск словом не в счёт: первая на
  // витрине не выставляется, второй уводит со страницы. Только витрина.
  const filterKey = (() => {
    const params = new URLSearchParams(searchParams);
    params.delete('sortBy');
    return params.toString();
  })();
  const prevFilterKey = useRef(filterKey);
  useEffect(() => {
    if (prevFilterKey.current === filterKey) return;
    prevFilterKey.current = filterKey;
    if (!showcaseBar) return;
    const panel = filtersRef.current;
    const grid = panel?.parentElement?.querySelector('.cars-page__grid');
    if (!panel || !grid) return;
    // Новая выдача уже отрисована (эффект после коммита): ставим её верх под
    // нижний край прилипшей панели с зазором ритма (32).
    const panelBottom = panel.getBoundingClientRect().bottom;
    const gap = 32;
    const gridTop = grid.getBoundingClientRect().top;
    if (gridTop < panelBottom + gap) {
      window.scrollTo({
        top: window.scrollY + gridTop - panelBottom - gap,
        behavior: 'smooth',
      });
    }
  }, [filterKey, showcaseBar]);

  const setListParam = (key, values, extraDelete) => {
    const newParams = new URLSearchParams(searchParams);
    if (values.length > 0) newParams.set(key, values.join(','));
    else newParams.delete(key);
    if (extraDelete) newParams.delete(extraDelete);
    setSearchParams(newParams);
  };
  const setNumParam = (key, val) => {
    const newParams = new URLSearchParams(searchParams);
    if (val === null) newParams.delete(key);
    else newParams.set(key, val);
    setSearchParams(newParams);
  };
  const toggleListValue = (key, list, value) =>
    setListParam(
      key,
      list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
    );

  // Быстрые чипы — самые частые кузова и виды топлива во ВСЁМ стоке, с числом
  // машин в текущей выдаче. Порядок — по всему стоку, а не по выдаче: иначе
  // чипы менялись местами под курсором после каждого нажатия. Остальные — в
  // «More». Выбранный чип остаётся в ряду, даже если он редкий: иначе снять
  // его можно было бы только через «More».
  const hotFilterOn = specialOffers || hotOfferOnly;
  const withHot = (cars) =>
    hotFilterOn ? cars.filter((car) => car.isHotOffer === true) : cars;
  const countBy = (cars, pick) => {
    const counts = new Map();
    cars.forEach((car) =>
      pick(car).forEach((v) => v && counts.set(v, (counts.get(v) || 0) + 1))
    );
    return counts;
  };
  const topChips = (rankCounts, counts, selected, limit) => {
    const ranked = [...rankCounts.entries()].sort((a, b) => b[1] - a[1]);
    const top = ranked.slice(0, limit).map(([v]) => v);
    selected.forEach((v) => !top.includes(v) && top.push(v));
    return top.map((v) => [v, counts.get(v) || 0]);
  };
  const bodyCounts = showcaseBar
    ? countBy(withHot(getFilteredCarsExcept('bodyStyle')), (car) =>
        splitBodyStyle(car.bodyStyle)
      )
    : new Map();
  const fuelCounts = showcaseBar
    ? countBy(withHot(getFilteredCarsExcept('fuelType')), (car) => [
        car.fuelType,
      ])
    : new Map();
  const bodyRank = showcaseBar
    ? countBy(allCars, (car) => splitBodyStyle(car.bodyStyle))
    : new Map();
  const fuelRank = showcaseBar
    ? countBy(allCars, (car) => [car.fuelType])
    : new Map();
  const hotCount = showcaseBar
    ? getFilteredCarsExcept(null).filter((car) => car.isHotOffer === true)
        .length
    : 0;
  const showcaseChips = showcaseBar
    ? [
        ...topChips(bodyRank, bodyCounts, bodyStyleFilter, 4).map(([v, n]) => ({
          key: `body-${v}`,
          label: translateVehicleValue(v, 'bodyStyle'),
          count: n,
          selected: bodyStyleFilter.includes(v),
          onClick: () => toggleListValue('bodyStyle', bodyStyleFilter, v),
        })),
        ...topChips(fuelRank, fuelCounts, fuelTypeFilter, 3).map(([v, n]) => ({
          key: `fuel-${v}`,
          label: translateVehicleValue(v, 'fuelType'),
          count: n,
          selected: fuelTypeFilter.includes(v),
          onClick: () => toggleListValue('fuelType', fuelTypeFilter, v),
        })),
        {
          key: 'special',
          label: t('showcase.catalog.specialOffers', 'Special offers'),
          count: hotCount,
          selected: specialOffers,
          onClick: () => setNumParam('specialOffers', specialOffers ? null : 'true'),
        },
      ]
    : [];

  // Применённые фильтры — по тегу на фильтр (#1335): их показывает
  // прилипшая панель витрины, у каждого — снять одним нажатием. Подписи те
  // же, что у полей; значения — через запятую.
  const rangeLabel = (min, max, fmt, fromLabel) => {
    if (min && max) return `${fmt(min)}–${fmt(max)}`;
    if (min) return `${fromLabel} ${fmt(min)}`;
    return `${showcaseText('showcase.catalog.to', 'To')} ${fmt(max)}`;
  };
  const param = (key) => searchParams.get(key);
  const appliedFilters = !showcaseBar
    ? []
    : [
        makeFilter.length > 0 && {
          key: 'make',
          label: makeFilter.join(', '),
          onRemove: () => setListParam('make', [], 'model'),
        },
        modelFilter.length > 0 && {
          key: 'model',
          label: modelFilter.join(', '),
          onRemove: () => setListParam('model', []),
        },
        (rawMinPrice !== null || rawMaxPrice !== null) && {
          key: 'price',
          label:
            priceBandOptions.find((o) => o.value === selectedPriceBand && o.value)
              ?.label || t('showcase.catalog.price', 'Price'),
          onRemove: () => handlePriceBandChange(''),
        },
        (param('minYear') || param('maxYear')) && {
          key: 'year',
          label: rangeLabel(
            param('minYear'),
            param('maxYear'),
            (v) => v,
            showcaseText('showcase.catalog.yearFrom', 'Year from')
          ),
          onRemove: () => {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('minYear');
            newParams.delete('maxYear');
            setSearchParams(newParams);
          },
        },
        bodyStyleFilter.length > 0 && {
          key: 'body',
          label: bodyStyleFilter
            .map((v) => translateVehicleValue(v, 'bodyStyle'))
            .join(', '),
          onRemove: () => setListParam('bodyStyle', []),
        },
        fuelTypeFilter.length > 0 && {
          key: 'fuel',
          label: fuelTypeFilter
            .map((v) => translateVehicleValue(v, 'fuelType'))
            .join(', '),
          onRemove: () => setListParam('fuelType', []),
        },
        transmissionFilter.length > 0 && {
          key: 'transmission',
          label: transmissionFilter
            .map((v) => translateVehicleValue(v, 'transmission'))
            .join(', '),
          onRemove: () => setListParam('transmission', []),
        },
        (param('minMileage') || param('maxMileage')) && {
          key: 'mileage',
          label: rangeLabel(
            param('minMileage'),
            param('maxMileage'),
            (v) => `${parseInt(v, 10).toLocaleString()} km`,
            showcaseText('showcase.catalog.mileageFrom', 'Mileage from')
          ),
          onRemove: () => {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('minMileage');
            newParams.delete('maxMileage');
            setSearchParams(newParams);
          },
        },
        availableOnly && {
          key: 'available',
          label: t('showcase.catalog.availableOnly', 'Available only'),
          onRemove: () => setNumParam('availableOnly', null),
        },
        specialOffers && {
          key: 'special',
          label: t('showcase.catalog.specialOffers', 'Special offers'),
          onRemove: () => setNumParam('specialOffers', null),
        },
      ].filter(Boolean);

  // Шапка фильтров витрины одной функцией: полная (в покое и раскрытая) и
  // компактная — в прилипшей панели только применённые фильтры тегами.
  const showcaseBarEl = (compact) => (
    <ShowcaseFilterBar
      compact={compact}
      applied={appliedFilters}
      onOpenAll={() =>
        isMobile ? setIsMobileFiltersOpen(true) : setIsFiltersExpanded(true)
      }
      isMobile={isMobile}
      fields={[
        <MultiSelect
          key="make"
          id="car-make-filter"
          placeholder={t('showcase.catalog.make', 'Make')}
          options={uniqueMakes}
          value={makeFilter}
          counts={facets?.make}
          onChange={(v) => setListParam('make', v, 'model')}
        />,
        <MultiSelect
          key="model"
          id="car-model-filter"
          placeholder={t('showcase.catalog.model', 'Model')}
          options={availableModels}
          value={modelFilter}
          counts={facets?.model}
          onChange={(v) => setListParam('model', v)}
        />,
        <Dropdown
          key="price"
          id="car-price"
          value={selectedPriceBand}
          onChange={handlePriceBandChange}
          options={priceBandOptions}
          placeholder={t('showcase.catalog.price', 'Price')}
        />,
        <RangeCombobox
          key="year"
          id="car-year"
          fromValue={
            searchParams.get('minYear')
              ? parseInt(searchParams.get('minYear'))
              : null
          }
          toValue={
            searchParams.get('maxYear')
              ? parseInt(searchParams.get('maxYear'))
              : null
          }
          onFromChange={(val) => setNumParam('minYear', val)}
          onToChange={(val) => setNumParam('maxYear', val)}
          fromOptions={yearFromOptions}
          toOptions={yearToOptions}
          fromPlaceholder={showcaseText('showcase.catalog.yearFrom', 'Year from')}
          toPlaceholder={showcaseText('showcase.catalog.to', 'To')}
        />,
      ]}
      moreFields={[
        <MultiSelect
          key="body"
          id="car-body-type-filter"
          placeholder={t('showcase.catalog.bodyType', 'Body type')}
          options={byLabel(uniqueBodyStyles, 'bodyStyle')}
          getOptionLabel={(v) => translateVehicleValue(v, 'bodyStyle')}
          value={bodyStyleFilter}
          counts={facets?.bodyStyle}
          onChange={(v) => setListParam('bodyStyle', v)}
        />,
        <MultiSelect
          key="fuel"
          id="car-fuel-type-filter"
          placeholder={t('showcase.catalog.fuelType', 'Fuel type')}
          options={byLabel(uniqueFuelTypes, 'fuelType')}
          getOptionLabel={(v) => translateVehicleValue(v, 'fuelType')}
          value={fuelTypeFilter}
          counts={facets?.fuelType}
          onChange={(v) => setListParam('fuelType', v)}
        />,
        // Коробка: фильтр по `?transmission=` в каталоге давно
        // есть (его ставит ассистент), поля в интерфейсе не было.
        <MultiSelect
          key="transmission"
          id="car-transmission-filter"
          placeholder={t('showcase.car.transmission', 'Transmission')}
          options={byLabel(uniqueTransmissions, 'transmission')}
          getOptionLabel={(v) =>
            translateVehicleValue(v, 'transmission')
          }
          value={transmissionFilter}
          counts={facets?.transmission}
          onChange={(v) => setListParam('transmission', v)}
        />,
        <RangeCombobox
          key="mileage"
          id="car-mileage"
          fromValue={
            searchParams.get('minMileage')
              ? parseInt(searchParams.get('minMileage'))
              : null
          }
          toValue={
            searchParams.get('maxMileage')
              ? parseInt(searchParams.get('maxMileage'))
              : null
          }
          onFromChange={(val) => setNumParam('minMileage', val)}
          onToChange={(val) => setNumParam('maxMileage', val)}
          fromOptions={mileageFromOptions}
          toOptions={mileageToOptions}
          fromPlaceholder={showcaseText('showcase.catalog.mileageFrom', 'Mileage from')}
          toPlaceholder={showcaseText('showcase.catalog.to', 'To')}
          formatOption={(val) =>
            val === 0 ? 'Any' : `${val.toLocaleString()} km`
          }
        />,
      ]}
      moreOpen={isFiltersExpanded}
      onToggleMore={() => setIsFiltersExpanded(!isFiltersExpanded)}
      moreCount={
        (bodyStyleFilter.length > 0 ? 1 : 0) +
        (fuelTypeFilter.length > 0 ? 1 : 0) +
        (transmissionFilter.length > 0 ? 1 : 0) +
        (minMileage !== mileageRange.min ||
        maxMileage !== mileageRange.max
          ? 1
          : 0)
      }
      activeCount={activeFiltersCount}
      chips={showcaseChips}
      availableToggle={
        <AvailableOnlyFilter
          checked={availableOnly}
          onChange={(checked) =>
            setNumParam('availableOnly', checked ? 'true' : null)
          }
        />
      }
      countText={t(
        'showcase.catalog.showing',
        'Showing {shown} of {total} cars'
      )
        .replace('{shown}', displayedCars.length)
        .replace('{total}', allCars.length)}
      hasFilters={hasAnyFilters}
      onClear={clearFilters}
    />
  );

  // Mini-catalog mode (#828): компактный layout без chrome (filter panel, sort,
  // counter, sticky scroll). Используется для встраивания на главную/промо-страницы
  // клиентского сайта, где нужен только блок карточек.
  // (isMiniCatalog объявлен выше, рядом с useDutyFree — см. комментарий там.)

  return (
    <div className={isMiniCatalog ? '' : 'cars-page'}>
      <div
        className={
          isMiniCatalog
            ? /* mini-catalog: сетка по ширине виджета, а не вьюпорта (#828) —
           контейнерные запросы описаны в CarsPage.css */
              'cars-page__mini'
            : 'cars-page__container'
        }
      >
        {/* Заголовок страницы — ТОЛЬКО на витрине. До этого каталог был
            единственной её страницей, которая начиналась сразу с фильтров: ни
            `h1`, ни названия — и в обходе с клавиатуры и читалкой страница
            оставалась безымянной.

            У дилера заголовка нет ни в каком виде встраивания. Каталог едет к
            нему внутрь его собственной страницы, у которой свой `h1` уже
            есть: второй заголовок ломает ему структуру документа и читается
            чужим блоком посреди его вёрстки. Условие то же, по которому в
            widget mode прячутся шапка и подвал (`SHOW_HEADER` /
            `SHOW_FOOTER`), плюс отдельная проверка на mini-catalog — он
            встраивается и в standalone. */}
        {!isMiniCatalog && config.ENVIRONMENT_NAME !== 'widget' && (
          <h1 className="cars-page__title">
            {t('showcase.catalog.title', 'Our cars')}
          </h1>
        )}

        {/* Placeholder для sticky позиционирования — НЕ нужен в mini-catalog */}
        {!isMiniCatalog && (
          <div
            ref={filtersPlaceholderRef}
            className={
              isSticky
                ? isMobile
                  ? 'cars-page__sticky-spacer cars-page__sticky-spacer--mobile'
                  : 'cars-page__sticky-spacer'
                : ''
            }
          />
        )}

        {/* Filters panel:
            - В full catalog — всегда виден (default behavior).
            - В mini-catalog — скрыт по умолчанию (#828); включается явно через
              `showFiltersPaneOverride === true` из embed config (#945). */}
        {(!isMiniCatalog || showFiltersPaneOverride === true) && (
          <div
            ref={filtersRef}
            className={[
              'cars-page__filters',
              isMobile && isSticky && isMobileFiltersOpen
                ? 'cars-page__filters--scroll'
                : '',
              !isMobile ? 'cars-page__filters--animated' : '',
              isSticky ? 'cars-page__filters--sticky' : 'filters-panel',
              isMobile && isSticky && !isMobileFiltersOpen && !showcaseBar
                ? 'cars-page__filters--bare'
                : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={
              isSticky
                ? {
                    top: `var(--header-height, ${
                      config.SHOW_HEADER ? '64px' : '100px'
                    })`,
                    ...(isMobile && isMobileFiltersOpen
                      ? {
                          maxHeight: `calc(100vh - var(--header-height, ${
                            config.SHOW_HEADER ? '64px' : '100px'
                          }))`,
                          WebkitOverflowScrolling: 'touch',
                        }
                      : {}),
                  }
                : undefined
            }
            onScroll={() => {
              // Close all open dropdowns when filter panel scrolls
              // (fixed-positioned dropdowns don't scroll with the panel)
              window.dispatchEvent(new CustomEvent('dropdown-close-all'));
            }}
          >
            {/* Mobile collapsed button - показываем только на мобилке в sticky режиме */}
            {isMobile && isSticky && !isMobileFiltersOpen && showcaseBar &&
              showcaseBarEl(true)}
            {isMobile && isSticky && !isMobileFiltersOpen && !showcaseBar && (
              <div className="cars-page__handle">
                <Button
                  variant="ghost"
                  size="default"
                  onClick={() => setIsMobileFiltersOpen(true)}
                  className="cars-page__handle"
                >
                  <ListFilter size={20} />
                  <span>{t('showcase.catalog.filters', 'Filters')}</span>
                  {hasAnyFilters && (
                    <span className="cars-page__badge cars-page__badge--wide">
                      {activeFiltersCount}
                    </span>
                  )}
                </Button>
              </div>
            )}

            {/* Full filters - скрываем на мобилке в sticky режиме, если не раскрыто */}
            <div
              ref={filtersContentRef}
              className={[
                isMobile && isSticky && !isMobileFiltersOpen
                  ? 'cars-page__filters-body--collapsed'
                  : '',
                isMobile && !isSticky ? 'animate-fadeIn' : '',
                isSticky ? 'cars-page__filters-body--sticky' : '',
                !isMobile || !isSticky || isMobileFiltersOpen
                  ? 'cars-page__filters-body'
                  : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {showcaseBar &&
                showcaseBarEl(!isMobile && isSticky && !isFiltersExpanded)}

              {!showcaseBar && (
              <>
              {/* Row 1: Main filters + action buttons */}
              <div className="cars-page__filter-row">
                {/* Make */}
                <div className="cars-page__filter cars-page__filter--w180">
                  <MultiSelect
                    id="car-make-filter"
                    placeholder={t('showcase.catalog.make', 'Make')}
                    options={uniqueMakes}
                    value={makeFilter}
                    counts={facets?.make}
                    onChange={(selectedMakes) => {
                      const newParams = new URLSearchParams(searchParams);
                      if (selectedMakes.length > 0) {
                        newParams.set('make', selectedMakes.join(','));
                      } else {
                        newParams.delete('make');
                      }
                      newParams.delete('model');
                      setSearchParams(newParams);
                    }}
                  />
                </div>

                {/* Price + Year Range - side by side on mobile */}
                <div className="cars-page__actions">
                  <div className="cars-page__filter cars-page__filter--w150">
                    <Dropdown
                      id="car-price"
                      value={selectedPriceBand}
                      onChange={handlePriceBandChange}
                      options={priceBandOptions}
                      placeholder={t('showcase.catalog.price', 'Price')}
                    />
                  </div>
                  <div className="cars-page__filter">
                    <RangeCombobox
                      id="car-year"
                      fromValue={
                        searchParams.get('minYear')
                          ? parseInt(searchParams.get('minYear'))
                          : null
                      }
                      toValue={
                        searchParams.get('maxYear')
                          ? parseInt(searchParams.get('maxYear'))
                          : null
                      }
                      onFromChange={(val) => {
                        const newParams = new URLSearchParams(searchParams);
                        if (val === null) newParams.delete('minYear');
                        else newParams.set('minYear', val);
                        setSearchParams(newParams);
                      }}
                      onToChange={(val) => {
                        const newParams = new URLSearchParams(searchParams);
                        if (val === null) newParams.delete('maxYear');
                        else newParams.set('maxYear', val);
                        setSearchParams(newParams);
                      }}
                      fromOptions={yearFromOptions}
                      toOptions={yearToOptions}
                      fromPlaceholder={showcaseText('showcase.catalog.yearFrom', 'Year from')}
                      toPlaceholder={showcaseText('showcase.catalog.to', 'To')}
                    />
                  </div>
                </div>

                {/* Desktop: Sort/More - order-1 on sm to stay on first line */}
                <div className="cars-page__toolbar">
                  {hasAnyFilters && (
                    <Button
                      id="car-clear-filters-btn-desktop"
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="cars-page__sort"
                    >
                      <X size={18} />
                      Clear
                    </Button>
                  )}
                  <Dropdown
                    value={sortBy}
                    onChange={(val) => updateFilter('sortBy', val)}
                    options={SORT_OPTIONS}
                    align="right"
                    dropdownClassName="narrow"
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="cars-page__icon-btn"
                    >
                      <ArrowUpDown size={20} />
                    </Button>
                  </Dropdown>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
                    className="cars-page__sort"
                  >
                    <ListFilter size={18} />
                    {isFiltersExpanded
                      ? t('showcase.catalog.less', 'Less')
                      : t('showcase.catalog.more', 'More')}
                    {!isFiltersExpanded && secondaryFiltersCount > 0 && (
                      <span className="cars-page__badge">
                        {secondaryFiltersCount}
                      </span>
                    )}
                  </Button>
                </div>

                {/* Тоглы лежат В первой строке фильтров, а не отдельным рядом
                    под ней: в первой строке между полями и кнопками сортировки
                    оставался пустой промежуток в половину экрана (замер на
                    1440: поля кончались на 681, кнопки начинались на 1260), а
                    тоглы занимали второй ряд шириной в 300px.

                    ⚠️ По умолчанию `flex-basis: 100%` оставляет их на своей
                    строке — ровно как было. Иначе правка доехала бы до каталога
                    дилера: `CarsPage` уезжает клиенту через `registry/Catalog`.
                    Перенос в первую строку включён только для витрины, из
                    `app-shell.css`. */}
                <div className="cars-page__meta">
                  <AvailableOnlyFilter
                    checked={availableOnly}
                    onChange={(checked) => {
                      const newParams = new URLSearchParams(searchParams);
                      if (checked) {
                        newParams.set('availableOnly', 'true');
                      } else {
                        newParams.delete('availableOnly');
                      }
                      setSearchParams(newParams);
                    }}
                  />
                  <SpecialOffersFilter
                    checked={specialOffers}
                    onChange={(checked) => {
                      const newParams = new URLSearchParams(searchParams);
                      if (checked) {
                        newParams.set('specialOffers', 'true');
                      } else {
                        newParams.delete('specialOffers');
                      }
                      setSearchParams(newParams);
                    }}
                  />
                  {showDutyFreeCheckbox &&
                    (dutyFreePricesActive ? (
                      <DutyFreeCheckbox
                        showTooltip={false}
                        checked={isDutyFree}
                        onChange={handleDutyFreeToggle}
                      />
                    ) : (
                      <DutyFreeCheckbox showTooltip={false} />
                    ))}
                </div>
              </div>

              {/* Expanded filters accordion: Mileage, Body Type, Fuel Type */}
              <div
                className={`cars-page__more${
                  isFiltersExpanded ? ' cars-page__more--open' : ''
                }`}
              >
                <div className="cars-page__filter-row cars-page__filter-row--loose">
                  <div className="cars-page__filter-pair">
                    <div className="cars-page__filter cars-page__filter--w180">
                      <MultiSelect
                        id="car-model-filter"
                        placeholder={t('showcase.catalog.model', 'Model')}
                        options={availableModels}
                        value={modelFilter}
                        counts={facets?.model}
                        onChange={(selectedModels) => {
                          const newParams = new URLSearchParams(searchParams);
                          if (selectedModels.length > 0) {
                            newParams.set('model', selectedModels.join(','));
                          } else {
                            newParams.delete('model');
                          }
                          setSearchParams(newParams);
                        }}
                      />
                    </div>
                    <div className="cars-page__filter">
                      <RangeCombobox
                        id="car-mileage"
                        fromValue={
                          searchParams.get('minMileage')
                            ? parseInt(searchParams.get('minMileage'))
                            : null
                        }
                        toValue={
                          searchParams.get('maxMileage')
                            ? parseInt(searchParams.get('maxMileage'))
                            : null
                        }
                        onFromChange={(val) => {
                          const newParams = new URLSearchParams(searchParams);
                          if (val === null) newParams.delete('minMileage');
                          else newParams.set('minMileage', val);
                          setSearchParams(newParams);
                        }}
                        onToChange={(val) => {
                          const newParams = new URLSearchParams(searchParams);
                          if (val === null) newParams.delete('maxMileage');
                          else newParams.set('maxMileage', val);
                          setSearchParams(newParams);
                        }}
                        fromOptions={mileageFromOptions}
                        toOptions={mileageToOptions}
                        fromPlaceholder={showcaseText('showcase.catalog.mileageFrom', 'Mileage from')}
                        toPlaceholder={showcaseText('showcase.catalog.to', 'To')}
                        formatOption={(val) =>
                          val === 0 ? 'Any' : `${val.toLocaleString()} km`
                        }
                      />
                    </div>
                    <MultiSelect
                      id="car-body-type-filter"
                      placeholder={t('showcase.catalog.bodyType', 'Body type')}
                      options={byLabel(uniqueBodyStyles, 'bodyStyle')}
                      getOptionLabel={(v) =>
                        translateVehicleValue(v, 'bodyStyle')
                      }
                      value={bodyStyleFilter}
                      counts={facets?.bodyStyle}
                      onChange={(selected) => {
                        const newParams = new URLSearchParams(searchParams);
                        if (selected.length > 0) {
                          newParams.set('bodyStyle', selected.join(','));
                        } else {
                          newParams.delete('bodyStyle');
                        }
                        setSearchParams(newParams);
                      }}
                      className="cars-page__filter cars-page__filter--w200"
                    />
                    <MultiSelect
                      id="car-fuel-type-filter"
                      placeholder={t('showcase.catalog.fuelType', 'Fuel type')}
                      options={byLabel(uniqueFuelTypes, 'fuelType')}
                      getOptionLabel={(v) =>
                        translateVehicleValue(v, 'fuelType')
                      }
                      value={fuelTypeFilter}
                      counts={facets?.fuelType}
                      onChange={(selected) => {
                        const newParams = new URLSearchParams(searchParams);
                        if (selected.length > 0) {
                          newParams.set('fuelType', selected.join(','));
                        } else {
                          newParams.delete('fuelType');
                        }
                        setSearchParams(newParams);
                      }}
                      className="cars-page__filter cars-page__filter--w200"
                    />
                  </div>
                </div>
              </div>
              </>
              )}

              {/* Search chip — shows the active ?search= query so user understands
                 why the catalog is filtered + sorted by relevance. Clearing it
                 removes the URL param, which in turn drops meliHitRanks → catalog
                 falls back to default menu-index sort and shows all cars. */}
              {searchTerm && (
                <div className="cars-page__chips">
                  <span className="cars-page__hint">
                    {t('showcase.catalog.searchHint', 'Search:')}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      const newParams = new URLSearchParams(searchParams);
                      newParams.delete('search');
                      setSearchParams(newParams);
                    }}
                    className="search-chip"
                    title="Clear search"
                    aria-label={`Clear search "${searchTerm}"`}
                  >
                    <span>{searchTerm}</span>
                    <X size={14} />
                  </button>
                </div>
              )}

              {/* AI-selection chip — makes the otherwise invisible offerId/offerIds
                 filter (applied by the assistant via set_filters) visible and
                 removable. Text comes from ?offerIdsLabel=, falling back to a
                 generic "AI selection". Clearing drops the id-filter + label →
                 the catalog expands back to the remaining filters. */}
              {hasAgentSelection && (
                <div className="cars-page__chips">
                  <button
                    type="button"
                    onClick={() => {
                      const newParams = new URLSearchParams(searchParams);
                      newParams.delete('offerId');
                      newParams.delete('offerIds');
                      newParams.delete('offerIdsLabel');
                      setSearchParams(newParams);
                    }}
                    className="search-chip"
                    title="Clear this selection"
                    aria-label={`Clear selection "${
                      offerIdsLabel || 'AI selection'
                    }"`}
                  >
                    <span>{offerIdsLabel || 'AI selection'}</span>
                    <X size={14} />
                  </button>
                </div>
              )}

              {/* Mobile: Clear, Sort and More buttons after accordion */}
              {!showcaseBar && (
              <div className="cars-page__toolbar--mobile">
                {hasAnyFilters && (
                  <Button
                    id="car-clear-filters-btn-mobile"
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="cars-page__sort cars-page__sort--sm"
                  >
                    <X size={14} />
                    Clear
                  </Button>
                )}
                <Dropdown
                  value={sortBy}
                  onChange={(val) => updateFilter('sortBy', val)}
                  options={SORT_OPTIONS}
                  align="left"
                  dropdownClassName="narrow"
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    className="cars-page__icon-btn cars-page__icon-btn--sm"
                  >
                    <ArrowUpDown size={16} />
                  </Button>
                </Dropdown>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
                  className="cars-page__sort cars-page__sort--sm"
                >
                  <ListFilter size={14} />
                  {isFiltersExpanded
                    ? t('showcase.catalog.less', 'Less')
                    : t('showcase.catalog.more', 'More')}
                  {!isFiltersExpanded && secondaryFiltersCount > 0 && (
                    <span className="cars-page__badge">
                      {secondaryFiltersCount}
                    </span>
                  )}
                </Button>
              </div>
              )}
            </div>
          </div>
        )}

        {/* Search bar — isolated component, typing doesn't re-render grid */}
        {config.SHOW_SEARCH_BAR && (
          <SearchBar
            onSearchResults={handleSearchResults}
            voiceAssistant={voiceAssistant}
            initialValue={searchTerm || config.PRESET_FILTERS?.search || ''}
          />
        )}

        {/* Results and Loading/Error States */}
        {loading ? (
          <div className="cars-page__loading">
            <Loader2 size={32} className="cars-page__spinner" />
            <span className="cars-page__loading-text">
              {t('showcase.catalog.loading', 'Loading cars...')}
            </span>
          </div>
        ) : error ? (
          <div className="cars-page__state">
            {/* Технический текст ошибки (`HTTP error! status: 500`) остаётся в
                консоли — его логирует CarsContext. Покупателю показываем
                действие, а не диагностику. */}
            <p className="cars-page__error">
              {t(
                'showcase.catalog.loadError',
                'Couldn\u2019t load the catalog. Please try again.'
              )}
            </p>
            <Button onClick={() => window.location.reload()}>
              {t('showcase.catalog.retry', 'Retry')}
            </Button>
          </div>
        ) : (
          <>
            {/* Counter — скрыт в mini-catalog mode (#828); на витрине число
                машин стоит в строке чипов (`ShowcaseFilterBar`). */}
            {!isMiniCatalog && !showcaseBar && (
              <div className="cars-page__section">
                <p className="cars-page__muted">
                  {t(
                    'showcase.catalog.showing',
                    'Showing {shown} of {total} cars'
                  )
                    .replace('{shown}', displayedCars.length)
                    .replace(
                      '{total}',
                      maxCars != null &&
                        sortedCars.length > displayedCars.length
                        ? sortedCars.length
                        : allCars.length
                    )}
                </p>
              </div>
            )}

            {/* Cars Grid */}
            {displayedCars.length > 0 ? (
              <div
                // Витрина: новая выдача проявляется после смены фильтра
                // (#1335), а не подменяет старую рывком — ключ по фильтрам
                // перезапускает анимацию появления.
                key={showcaseBar ? filterKey : undefined}
                className={
                  isMiniCatalog
                    ? /* контейнерные запросы: 672px → 2 колонки, 896px → 3 */
                      'cars-page__mini-grid'
                    : `cars-page__grid${showcaseBar ? ' cars-page__grid--fade' : ''}`
                }
              >
                {displayedCars.map((car) => (
                  <CarCard
                    key={car.id}
                    car={car}
                    onCardClick={handleCardClick}
                    onBookTestDrive={handleBookTestDrive}
                    showMonthlyRates={showMonthlyRatesOverride === true}
                  />
                ))}
              </div>
            ) : (
              <div className="cars-page__state cars-page__state--sm">
                <p className="cars-page__state-title">
                  {t(
                    'showcase.catalog.empty',
                    'No cars found matching your criteria'
                  )}
                </p>
                <Button onClick={clearFilters}>
                  {t('showcase.catalog.clear', 'Clear filters')}
                </Button>
              </div>
            )}
          </>
        )}

        <TestDriveModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          selectedCar={selectedCar}
          cars={allCars}
        />
      </div>
    </div>
  );
};

export default CarsPage;
